import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { LoadingShimmerModule } from 'loading-shimmer';
import { MaskitoOptions } from '@maskito/core';
import { BehaviorSubject, combineLatest, Observable, of, retry } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { AddressFormGroup } from '../../../forms/address-form-group';
import { DropDownItem } from '../../../models/shared/stylesheet/drop-down-item';
import { AddressFormService } from '../../../services/address-form.service';
import { GeographyService } from '../../../services/geography/geography.service';
import { TextInputComponent } from '../../form-components/text-input/text-input.component';
import { DropdownInputComponent } from '../../form-components/dropdown-input/dropdown-input.component';
import { FormErrorComponent } from '../../form-components/form-error/form-error.component';
import { MaskedTextInputComponent } from '../../form-components/masked-text-input/masked-text-input.component';
import { CANADA_COUNTRY_ID } from '../../../consts/consts';

const COUNTRIES_RETRY_DELAY_MS = 2000;
const POSTAL_CODE_CHARACTER = /[A-Za-z0-9]/;

type AddressErrorMessageKey = 'countryId' | 'addressLine1' | 'city' | 'stateId' | 'postalCode';
type AddressErrorMessages = Record<AddressErrorMessageKey, Record<string, string>>;
export type AddressErrorMessageOverrides = Partial<AddressErrorMessages>;

const DEFAULT_ERROR_MESSAGES = (canadaOnly: boolean): AddressErrorMessages => ({
  countryId: {
    required: $localize`Country is required`
  },
  addressLine1: {
    required: $localize`Address 1 is required`
  },
  city: {
    required: $localize`City/Town is required`
  },
  stateId: {
    required: canadaOnly ? $localize`Province is required` : $localize`Province/State/Region is required`
  },
  postalCode: {
    required: canadaOnly ? $localize`Postal Code is required` : $localize`Postal Code/ZIP is required`,
    invalidPostalCode: $localize`Invalid postal code`,
    minlength: $localize`Invalid postal code`,
    maxlength: $localize`Invalid postal code`
  }
});

function mergeErrorMessages(
  defaults: AddressErrorMessages,
  overrides: AddressErrorMessageOverrides
): AddressErrorMessages {
  const merged = { ...defaults };
  (Object.keys(overrides) as AddressErrorMessageKey[]).forEach(key => {
    merged[key] = { ...defaults[key], ...overrides[key] };
  });
  return merged;
}

@Component({
  selector: 'app-international-address-form-grid',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LoadingShimmerModule,
    TextInputComponent,
    DropdownInputComponent,
    FormErrorComponent,
    MaskedTextInputComponent
  ],
  templateUrl: './international-address-form-grid.component.html',
  styleUrls: ['./international-address-form-grid.component.scss'],
  providers: [AddressFormService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InternationalAddressFormGridComponent implements OnChanges {
  @Input() form!: AddressFormGroup;
  @Input() formSubmitted = false;
  @Input() controlIdPrefix = 'internationalAddress';
  @Input() canadaOnly = false;
  @Input() threeColumnLayout = false;

  /**
   * Error copy that replaces this grid's own, per control and per error key. Everything not named
   * keeps the default, so a caller overrides only the strings its design words differently.
   */
  @Input() set errorMessageOverrides(overrides: AddressErrorMessageOverrides) {
    this.overrides = overrides;
    this.customErrorMessages = mergeErrorMessages(DEFAULT_ERROR_MESSAGES(this.canadaOnly), overrides);
  }

  @Input() set isLoading(isLoading: boolean) {
    this._isLoading.next(isLoading);
  }

  private geographyService = inject(GeographyService);
  private addressFormService = inject(AddressFormService);

  private _isLoading = new BehaviorSubject(false);
  private overrides: AddressErrorMessageOverrides = {};

  public isLoading$ = combineLatest([
    this._isLoading.asObservable(),
    this.geographyService.countriesLoading$,
    this.geographyService.statesLoading$
  ]).pipe(map(loadingFlags => loadingFlags.some(Boolean)));

  public countryDropdowns$ = this.geographyService.countries$.pipe(
    retry({ count: 1, delay: COUNTRIES_RETRY_DELAY_MS }),
    map(countries => countries.map(country => new DropDownItem(country.name, country.id))),
    catchError(() => of([] as DropDownItem[])),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  public stateDropdowns$: Observable<DropDownItem[]> = this.addressFormService.selectedCountryId$.pipe(
    switchMap(countryId =>
      this.geographyService.statesForCountry(countryId).pipe(
        map(states => states.map(state => new DropDownItem(state.name, state.id))),
        catchError(() => of([] as DropDownItem[]))
      )
    ),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  /** Countries with no states of their own hide the field outright rather than show an empty dropdown. */
  public stateFieldHidden$ = this.stateDropdowns$.pipe(map(stateDropdowns => stateDropdowns.length === 0));

  public stateAndPostalCodeOptional$ = this.addressFormService.stateAndPostalCodeOptional$;

  public postalCodeIsMasked$ = this.addressFormService.selectedCountryId$.pipe(
    map(countryId => countryId === CANADA_COUNTRY_ID)
  );

  /**
   * Slots accept any letter or digit, not the letter/digit alternation of a real postal code. Maskito
   * re-checks every slot after a delete and discards shifted characters that no longer fit, so strict
   * slots would remove more than the character the user deleted. The validators enforce the pattern.
   */
  public postalCodeMaskOptions: MaskitoOptions = {
    mask: [
      POSTAL_CODE_CHARACTER,
      POSTAL_CODE_CHARACTER,
      POSTAL_CODE_CHARACTER,
      ' ',
      POSTAL_CODE_CHARACTER,
      POSTAL_CODE_CHARACTER,
      POSTAL_CODE_CHARACTER
    ],
    postprocessors: [({ value, selection }) => ({ value: value.toUpperCase(), selection })]
  };

  public controlIds = InternationalAddressFormGridComponent.buildControlIds('internationalAddress');

  public stateLabel = $localize`Province/State/Region`;
  public postalCodeLabel = $localize`Postal Code/ZIP`;

  public labelShimmerHeight = '1.25rem';
  public inputShimmerHeight = '3.5rem';

  public customErrorMessages: AddressErrorMessages = DEFAULT_ERROR_MESSAGES(false);

  constructor() {
    this.countryDropdowns$.pipe(takeUntilDestroyed()).subscribe();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.controlIdPrefix) {
      this.controlIds = InternationalAddressFormGridComponent.buildControlIds(this.controlIdPrefix);
    }

    if (changes.canadaOnly) {
      this.stateLabel = this.canadaOnly ? $localize`Province` : $localize`Province/State/Region`;
      this.postalCodeLabel = this.canadaOnly ? $localize`Postal Code` : $localize`Postal Code/ZIP`;
      this.customErrorMessages = mergeErrorMessages(DEFAULT_ERROR_MESSAGES(this.canadaOnly), this.overrides);
    }

    if ((changes.form || changes.canadaOnly) && this.form && this.canadaOnly) {
      this.form.controls.countryId.setValue(CANADA_COUNTRY_ID);
    }

    if (changes.form && this.form) {
      this.addressFormService.manageInternationalAddressControls(
        this.form.controls.countryId,
        this.form.controls.stateId,
        this.form.controls.postalCode
      );
    }
  }

  private static buildControlIds(prefix: string): Record<keyof AddressFormGroup['controls'], string> {
    return {
      countryId: `${prefix}Country`,
      addressLine1: `${prefix}AddressLine1`,
      addressLine2: `${prefix}AddressLine2`,
      addressLine3: `${prefix}AddressLine3`,
      city: `${prefix}City`,
      stateId: `${prefix}State`,
      postalCode: `${prefix}PostalCode`
    };
  }
}
